import { useCollection } from '@dovefiniscono/sync/react';
import { useMemo } from 'react';
import { ancestorsOf, type Category, MAX_CATEGORY_DEPTH, moneyContext } from './summary';

/** A category with the ones right below it (up to three levels: Cena › Cena fuori › Sushi). */
export type CategoryNode = { category: Category; children: CategoryNode[] };

/** Categories of one kind as a tree, in order. */
function treeOf(
  categories: readonly Category[],
  parentOf: ReadonlyMap<string, string>,
): CategoryNode[] {
  const below = (parent: string | null, depth: number): CategoryNode[] =>
    depth > MAX_CATEGORY_DEPTH
      ? []
      : categories
          .filter((c) => (parent === null ? !parentOf.has(c.id) : parentOf.get(c.id) === parent))
          .map((category) => ({ category, children: below(category.id, depth + 1) }));
  return below(null, 1);
}

/** Every finance collection, with active items sorted and quick lookups by id. */
export function useMoney() {
  const transactions = useCollection('transactions');
  const allAccounts = useCollection('moneyAccounts');
  const allCategories = useCollection('categories');
  const budgets = useCollection('budgets');
  const allGoals = useCollection('savingsGoals');
  const invoices = useCollection('invoices');
  const plannedIncomes = useCollection('plannedIncomes');
  const obligations = useCollection('obligations');
  const plannedPurchases = useCollection('plannedPurchases');
  const fiscalYears = useCollection('fiscalYears');
  const allVehicles = useCollection('vehicles');
  const vehicleTrips = useCollection('vehicleTrips');

  return useMemo(() => {
    const byPosition = <T extends { position: number }>(a: T, b: T) => a.position - b.position;
    const accounts = allAccounts.filter((a) => a.archivedAt === null).sort(byPosition);
    const categories = allCategories.filter((c) => c.archivedAt === null).sort(byPosition);
    const context = moneyContext(allAccounts, allCategories);
    const categoryName = new Map(allCategories.map((c) => [c.id, c.name]));
    const expenseCategories = categories.filter((c) => c.kind === 'expense');
    const incomeCategories = categories.filter((c) => c.kind === 'income');
    return {
      transactions,
      accounts,
      /** Your own accounts: where your income lands and your balances live. */
      ownAccounts: accounts.filter((a) => a.kind !== 'external'),
      /** Who pays for you: parents, a shared fund. */
      externalAccounts: accounts.filter((a) => a.kind === 'external'),
      categories,
      expenseCategories,
      incomeCategories,
      expenseTree: treeOf(expenseCategories, context.parentOf),
      incomeTree: treeOf(incomeCategories, context.parentOf),
      budgets,
      goals: allGoals.filter((g) => g.archivedAt === null).sort(byPosition),
      vehicles: allVehicles.filter((v) => v.archivedAt === null).sort(byPosition),
      vehicleTrips,
      /** Real invoices: drafts made by rules stay out until you confirm them. */
      invoices: invoices.filter((invoice) => !invoice.draft),
      draftInvoices: invoices.filter((invoice) => invoice.draft),
      plannedIncomes,
      obligations,
      plannedPurchases,
      fiscalYears,
      context,
      accountName: new Map(allAccounts.map((a) => [a.id, a.name])),
      categoryName,
      /** The whole path for a subcategory ("Cena › Cena fuori › Sushi"), the name alone otherwise. */
      categoryLabel(id: string | null): string | null {
        if (!id) return null;
        const name = categoryName.get(id);
        if (name === undefined) return null;
        const above = ancestorsOf(id, context.parentOf)
          .reverse()
          .map((ancestor) => categoryName.get(ancestor) ?? '');
        return [...above, name].join(' › ');
      },
      allCategories,
    };
  }, [
    transactions,
    allAccounts,
    allCategories,
    budgets,
    allGoals,
    invoices,
    plannedIncomes,
    obligations,
    plannedPurchases,
    fiscalYears,
    allVehicles,
    vehicleTrips,
  ]);
}

export type MoneyData = ReturnType<typeof useMoney>;
